import React from 'react'

import { useTaskAPI } from '../TaskAPIProvider'
import { useTreeState, TYPES } from '../TreeStateProvider'
import useTaskColumnSettings from '../../hooks/useTaskColumnSettings'

const ControlledInfiniteTaskTableStateContext = React.createContext(null)

const ControlledInfiniteTaskTableStateProvider = ({
  name,
  tasks,
  collapsable,
  fullPagination,
  children,
  table
}) => {
  const { tasks: allTasks, formatTask } = useTaskAPI()
  const { dispatch: treeDispatch, Actions: TreeActions } = useTreeState(
    TYPES.TASK
  )

  const formattedTasks = React.useMemo(() => {
    if (!tasks) {
      return []
    }

    return tasks.flatMap(task =>
      task.data.map(t => {
        const formatted = formatTask(t)
        return { ...formatted, ...(allTasks[formatted.id] ?? {}), table }
      })
    )
  }, [tasks, formatTask, allTasks, table])

  const [searchFilter, setSearchFilter] = React.useState('')
  const [expanded, setExpanded] = React.useState(collapsable ? false : true)
  const toggleExpanded = React.useCallback(() => setExpanded(old => !old), [])

  const columnSettings = useTaskColumnSettings(name)

  React.useEffect(() => {
    treeDispatch({ type: TreeActions.UPDATE, id: name, open: !expanded })
  }, [expanded, name, treeDispatch, TreeActions.UPDATE])

  return (
    <ControlledInfiniteTaskTableStateContext.Provider
      value={{
        // Base table values
        tasks: formattedTasks,
        expanded,
        toggleExpanded: collapsable ? toggleExpanded : undefined,
        name,
        searchFilter,
        setSearchFilter,
        id: null,
        canAdd: true,
        key: name,

        // Column Setting props
        ...columnSettings,

        // For task arrows
        fullPagination
      }}
    >
      {children}
    </ControlledInfiniteTaskTableStateContext.Provider>
  )
}

export default ControlledInfiniteTaskTableStateProvider

export const useControlledInfiniteTaskTableState = () =>
  React.useContext(ControlledInfiniteTaskTableStateContext)
